/* eslint-disable camelcase */
import { BodyText, IconSet } from '@postscript/components';
import React from 'react';

import { COMPLIANCE_AI_ELIGIBILITY_IN_APP } from 'components/admin/utils/feature-flags';
import { AiEligibleBadge } from 'components/conversations/components/AiEligibleBadge';
import { useSubscriberAiEligibility } from 'components/conversations/hooks/useSubscriberAiEligibility';
import { Subscriber } from 'components/sales/types';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { Row, SectionCard, SectionHeader } from '../components';
import {
  displayCustomerState,
  displayLifecycleStage,
  ROW_LABELS,
  SECTION_TITLES,
} from '../copy';
import { MessageDetails } from '../types';

const SECTION_ID = 'message-details-subscriber';

interface SubscriberDetailsSectionProps {
  details: Pick<MessageDetails, 'customer_states' | 'lifecycle_stages'>;
  subscriber?: Subscriber | null;
}

// Subscriber identity card. Each row renders only when its value is present, so
// the card degrades gracefully for subscribers missing a name or phone. The AI
// Eligible row surfaces the consent-based eligibility signal (matching the
// /conversations Subscriber Profile card) and only renders behind the
// COMPLIANCE_AI_ELIGIBILITY_IN_APP labs flag. Customer Status / Lifecycle Stage
// render as comma-joined plain text. Every row is display-only — the section
// header carries no navigation affordance.
export const SubscriberDetailsSection: React.FC<SubscriberDetailsSectionProps> =
  ({ details, subscriber = null }) => {
    const customer = subscriber?.customer ?? null;
    const fullName =
      customer && (customer.first_name || customer.last_name)
        ? [customer.first_name, customer.last_name].filter(Boolean).join(' ')
        : null;
    const phone = subscriber?.subscriber?.phone_number ?? null;
    const subscriberId = subscriber?.subscriber?.id;

    const { hasLabsFlag } = usePSLabs();
    const isAiEligibilityInAppEnabled = hasLabsFlag(
      COMPLIANCE_AI_ELIGIBILITY_IN_APP,
    );
    const { data: aiEligibility, isInitialLoading: aiEligibilityLoading } =
      useSubscriberAiEligibility(
        isAiEligibilityInAppEnabled ? subscriberId : undefined,
      );

    const showAiEligibleRow =
      isAiEligibilityInAppEnabled && subscriberId != null;
    const aiEligibleTooltipId = `message-details-ai-eligible-tooltip-${subscriberId}`;

    // With no payload rows and no subscriber identity there's nothing to
    // show — drop the card instead of rendering a bare section header.
    const hasAnyRow =
      Boolean(fullName) ||
      Boolean(phone) ||
      showAiEligibleRow ||
      details.customer_states.length > 0 ||
      details.lifecycle_stages.length > 0;
    if (!hasAnyRow) return null;

    return (
      <SectionCard ariaLabelledBy={SECTION_ID} flush>
        <SectionHeader id={SECTION_ID} icon={IconSet.User} divider>
          {SECTION_TITLES.subscriberDetails}
        </SectionHeader>

        {fullName && (
          <Row label={ROW_LABELS.name} readOnly>
            <BodyText size="small">{fullName}</BodyText>
          </Row>
        )}
        {phone && (
          <Row label={ROW_LABELS.phoneNumber} readOnly>
            <BodyText size="small">{phone}</BodyText>
          </Row>
        )}
        {showAiEligibleRow && (
          <Row label={ROW_LABELS.aiEligible} readOnly>
            <AiEligibleBadge
              data={aiEligibility}
              isLoading={aiEligibilityLoading}
              tooltipId={aiEligibleTooltipId}
            />
          </Row>
        )}

        {details.customer_states.length > 0 && (
          <Row label={ROW_LABELS.customerStatus} readOnly>
            <BodyText size="small">
              {details.customer_states
                .map((value) => displayCustomerState[value])
                .join(', ')}
            </BodyText>
          </Row>
        )}
        {details.lifecycle_stages.length > 0 && (
          <Row label={ROW_LABELS.lifecycleStage} readOnly>
            <BodyText size="small">
              {details.lifecycle_stages
                .map((value) => displayLifecycleStage[value])
                .join(', ')}
            </BodyText>
          </Row>
        )}
      </SectionCard>
    );
  };
